import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React from 'react';

import BenchlingConfig from 'BenchlingConfig';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {getNonBatchContainerContents} from 'constants/SampleConstants';
import {Tag} from 'folder-items/FileConstants';
import ExcludeByTypename from 'graphql-client/ExcludeByTypename';
import ExcludeInaccessible from 'graphql-client/ExcludeInaccessible';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {
  getAccessibleOrInaccessibleId,
  isInaccessible as isInaccessibleGQL,
} from 'graphql-client/InaccessibleInfo';
import {SampleControlsFeatureLevel} from 'inventory/sample-status/SampleControlsFeatureLevel';
import {hasMinimumSCRFeatureLevel} from 'inventory/sample-status/SampleControlsFeatureLevelUtil';
import {SampleRestrictionStatusLabel} from 'inventory/sample-status/SampleRestrictionStatusLabel';
import SampleStatusLabel from 'inventory/sample-status/SampleStatusLabel';
import {getRestrictionAccessHeaderText} from 'inventory/sample-status/SampleStatusPresentationUtils';
import TagValueViewer from 'metadata/TagValueViewer';
import getQuantityDisplayString from 'samples/getQuantityDisplayString';
import GridPreview from 'samples/grids/QueryingGridPreview';
import {formatGridCoordinates, getAvailableSpaceDisplayString} from 'samples/SampleUtil';
import {PLATE_SCHEMA_TYPE} from 'samples/storables/StorableSchemaConstants';
import * as TransferUtil from 'samples/transfers/TransferUtil';
import {isFailed} from 'schemas/SchemaValidationChecks';
import {SchemaValidationLongDisplayTextByStatus} from 'schemas/SchemaValidationConstants';
import Avatar from 'ui-library/avatar/Avatar';
import {AvatarSize} from 'ui-library/avatar/AvatarConstants';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import BenchChip from 'ui-library/chips/BenchChip';
import {ChipSize} from 'ui-library/chips/Chip';
import ChipPopoverContent from 'ui-library/chips/ChipPopoverContent';
import {generateKeyForSchemaField} from 'ui-library/chips/ChipPopoverContentUtils';
import * as Colors from 'ui-library/Colors';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import BenchlingIcon, {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import PartyList from 'ui-library/PartyList';
import {readableTime} from 'util/DateUtil';

import {StorableChipOrInaccessibleGraphQL} from '../../../graphql-fragments/inventory/__generated/StorableChipOrInaccessibleGraphQL';
import {StorableChipSummaryGraphQL} from '../../../graphql-fragments/inventory/__generated/StorableChipSummaryGraphQL';

type Plate = ExtractByTypename<StorableChipSummaryGraphQL, 'PlateNode'>;

type Box = ExtractByTypename<StorableChipSummaryGraphQL, 'BoxNode'>;

type ParentSchema = ExcludeInaccessible<
  NonNullable<ExcludeInaccessible<StorableChipOrInaccessibleGraphQL>['authenticatedParentStorable']>
>['storableSchema'];

type PlateSchemaInfo = ExtractByTypename<ParentSchema, 'PlateSchemaNode'>;

type StorableChipContainer = ExtractByTypename<StorableChipOrInaccessibleGraphQL, 'ContainerNode'>;

type StorableChipContainerContent = StorableChipContainer['contents'][number];

type ContainerContent = StorableChipContainerContent;

type ContainedItem = ExcludeByTypename<ContainerContent['containedItem'], 'BatchNode'>;

interface Props {
  /** The API identifier of the storable */
  id: string;
  /** Whether the storable is accessible to the current user */
  isInaccessible: boolean | undefined;
  /** The storable summary, if we already have it */
  storable?: StorableChipSummaryGraphQL;
  /**
   * A function to override how the contents of this storable are rendered. By default, renders a BenchChip
   * for each content. Intended for cases when a custom visual treatment for contents is necessary, as with
   * Plate Maps, which inject a color-coded swatch next to each content chip.
   */
  containedItemRenderer?: (id: string) => JSX.Element;
}

function isContainer(storable?: StorableChipOrInaccessibleGraphQL): storable is StorableChipContainer {
  return storable?.__typename === 'ContainerNode';
}

interface ContentWithoutBatches extends ContainerContent {
  containedItem: ContainedItem;
}

export function StorableChipPopoverContent(props: Props): JSX.Element | null {
  const {storable: baseStorable, isInaccessible} = props;

  if (baseStorable == null) {
    return null;
  } else if (isInaccessible) {
    return <>(No access)</>;
  }
  const storable = {...baseStorable, parentStorable: baseStorable.authenticatedParentStorable};

  function isPlateSchema(storableSchema: ParentSchema): storableSchema is PlateSchemaInfo {
    return storableSchema.__typename === 'PlateSchemaNode';
  }

  const isFixedPlateWell = (storable: StorableChipSummaryGraphQL): boolean => {
    const parentStorable = storable.authenticatedParentStorable;
    if (
      parentStorable == null ||
      isInaccessibleGQL(parentStorable) ||
      !isPlateSchema(parentStorable.storableSchema)
    ) {
      return false;
    }
    return parentStorable.storableSchema.plateSchemaType === PLATE_SCHEMA_TYPE.FIXED_PLATE;
  };

  const validationFailureBanner = isFailed(storable.validationState) ? (
    <div className={css(styles.bannerSpacing)}>
      <InlineBanner
        bannerType={InlineBannerType.ERROR}
        text={SchemaValidationLongDisplayTextByStatus.FAILED}
      />
    </div>
  ) : null;

  const restrictionStatusValue = (): null | JSX.Element =>
    hasMinimumSCRFeatureLevel(SampleControlsFeatureLevel.V2) &&
    isContainer(storable) &&
    !isFixedPlateWell(storable) ? (
      <SampleRestrictionStatusLabel item={storable} itemSupportsSampleStatus={true} />
    ) : null;

  const availabilityValue = (): null | JSX.Element =>
    isContainer(storable) && !isFixedPlateWell(storable) ? (
      <SampleStatusLabel
        item={{
          ...storable,
          checkOutStatusInfo: storable.checkOutStatusInfoJson,
        }}
        includeAuthorization={false}
        includeAvailability={true}
        includeMessage={true}
        renderAllStatuses={true}
        itemSupportsSampleStatus={true}
      />
    ) : null;

  const authorizationValue = (): null | JSX.Element =>
    hasMinimumSCRFeatureLevel(SampleControlsFeatureLevel.V1) &&
    isContainer(storable) &&
    !isFixedPlateWell(storable) ? (
      <SampleStatusLabel
        item={{
          ...storable,
          checkOutStatusInfo: storable.checkOutStatusInfoJson,
        }}
        includeAuthorization={true}
        includeAvailability={false}
        includeMessage={true}
        renderAllStatuses={true}
        itemSupportsSampleStatus={true}
      />
    ) : null;

  const renderContainedItem = (id: string): JSX.Element => (
    <BenchChip key={id} id={id} size={ChipSize.MINI} />
  );

  const contentsValue = (): null | string | JSX.Element => {
    if (!isContainer(storable)) {
      return null;
    }

    const contents: Array<StorableChipContainerContent> = storable.contents;
    if (contents == null || !contents.length) {
      return 'Container is empty';
    } else {
      return (
        <>
          {getNonBatchContainerContents<ContainerContent, ContentWithoutBatches>(contents).map(
            ({containedItem}) =>
              props.containedItemRenderer
                ? props.containedItemRenderer(getAccessibleOrInaccessibleId(containedItem))
                : renderContainedItem(getAccessibleOrInaccessibleId(containedItem))
          )}
        </>
      );
    }
  };

  const quantityValue = (): string | null => {
    if (!isContainer(storable)) {
      return null;
    }

    const {value, units} = storable.quantityJson;
    return TransferUtil.getLegacyMeasurementString({value, units});
  };

  const concentrationValue = (): string | null => {
    if (
      isContainer(storable) &&
      storable.contents.length === 1 &&
      storable.contents[0].concentrationJson.value != null
    ) {
      const {value, units} = storable.contents[0].concentrationJson;
      return TransferUtil.getLegacyMeasurementString({value, units});
    } else {
      return null;
    }
  };

  const positionValue = (): string | null =>
    isContainer(storable) && storable.gridCell && !isInaccessibleGQL(storable.gridCell)
      ? formatGridCoordinates(storable.gridCell.rowIndex, storable.gridCell.columnIndex)
      : null;

  const locationValue = (): null | string | JSX.Element => {
    const {parentStorable} = storable;
    if (props.isInaccessible || parentStorable == null) {
      return null;
    }
    if (isInaccessibleGQL(parentStorable)) {
      return '(No access)';
    }
    return (
      <>
        {parentStorable.storableSchema.iconId ? (
          <BenchlingIcon icon={parentStorable.storableSchema.iconId as BenchlingIconName} />
        ) : null}
        &nbsp;{parentStorable.name}
      </>
    );
  };

  const creatorValue = (): JSX.Element => (
    <>
      <Avatar entity={storable.creator} size={AvatarSize.XSMALL} />
      &nbsp;{storable.creator.name}
    </>
  );

  const sampleOwnersValue = (): JSX.Element | null =>
    hasMinimumSCRFeatureLevel(SampleControlsFeatureLevel.V1) &&
    isContainer(storable) &&
    storable.sampleRoles != null &&
    storable.sampleRoles.sampleOwners.length !== 0 ? (
      <PartyList parties={storable.sampleRoles.sampleOwners} maxParties={3} useAvatar={true} />
    ) : null;

  const capacity = (): string | null => {
    if (isContainer(storable)) {
      return null;
    }
    if (storable.occupiedCapacity == null || storable.totalCapacity == null) {
      return null;
    }
    return getAvailableSpaceDisplayString(storable.totalCapacity, storable.occupiedCapacity);
  };

  const expirationDate = (): JSX.Element | null => {
    if (!BenchlingConfig.INVENTORY_ENABLE_EXPIRATION_DATE || !isContainer(storable)) {
      return null;
    }
    if (storable.expirationInfo.expirationDate == null || !storable.expirationInfo.isTrackingEnabled) {
      return null;
    }
    return (
      <>
        {storable.expirationInfo.isExpired ? (
          <Tooltip text="Expired">
            <span className={classNames(css(styles.warningBadgeIcon), 'u-tiny-padding-right')}>
              <BenchlingIcon icon="warning" />
            </span>
          </Tooltip>
        ) : null}
        {readableTime(storable.expirationInfo.expirationDate, {showTime: true})}
      </>
    );
  };

  const freezeThawCount = (): string | null => {
    if (!BenchlingConfig.INVENTORY_ENABLE_FREEZE_THAW || !isContainer(storable)) {
      return null;
    }
    if (storable.freezeThawInfo.freezeThawCount == null || !storable.freezeThawInfo.isTrackingEnabled) {
      return null;
    }
    return storable.freezeThawInfo.freezeThawCount.toString();
  };

  const fields = [
    {
      label: 'Barcode',
      value: (
        <>
          {storable.storableSchema.iconId ? (
            <BenchlingIcon icon={storable.storableSchema.iconId as BenchlingIconName} />
          ) : null}
          &nbsp;{storable.barcode?.value}
        </>
      ),
    },
    {label: 'Name', value: storable.name},
    {label: 'Restriction status', value: restrictionStatusValue()},
    {
      label: getRestrictionAccessHeaderText(),
      value: authorizationValue(),
    },
    {label: 'Availability', value: availabilityValue()},
    {label: 'Schema', value: storable.storableSchema.name},
    {
      label: 'Project',
      value: storable.source && (
        <>
          <BenchlingIcon icon={ModelTypeConstants.PROJECT} />
          &nbsp;{storable.source.name}
        </>
      ),
    },
    {label: 'Location', value: locationValue()},
    {label: 'Position', value: positionValue()},
    {label: 'Contents', value: contentsValue()},
    {label: getQuantityDisplayString(), value: quantityValue()},
    {label: 'Concentration', value: concentrationValue()},
    {label: 'Creator', value: creatorValue()},
    {label: 'Created', value: readableTime(storable.createdAt)},
    {label: 'Sample owners', value: sampleOwnersValue()},
    {label: 'Available space', value: capacity()},
    {label: 'Expiration date', value: expirationDate()},
    {label: 'Freeze/Thaw count', value: freezeThawCount()},
    ...(storable.tagsJson as Array<Tag>).map((tag) => ({
      generateStableKey: () => generateKeyForSchemaField(tag),
      label: tag.name,
      value: (
        <TagValueViewer
          tag={tag}
          chipSize={ChipSize.MINI}
          shouldPreviewImages={true}
          showChipsAsLinks={true}
          summarize={true}
          summarizeThreshold={3}
          showSummaryChipsAsPopover={true}
        />
      ),
    })),
  ];

  const isGrid = (storable: StorableChipSummaryGraphQL): storable is Box | Plate =>
    ['BoxNode', 'PlateNode'].includes(storable.__typename);
  return (
    <>
      {isGrid(storable) && <GridPreview grid={storable} widthPx={244} />}
      {validationFailureBanner}
      <ChipPopoverContent info={{fields}} />
    </>
  );
}

const styles = StyleSheet.create({
  bannerSpacing: {
    margin: 4,
    marginTop: 6,
  },
  warningBadgeIcon: {
    color: Colors.ORANGE,
  },
});
